// ColorBlockLayer — solid, scene-aware color blocks behind the HUD.
// Opacity is driven by scroll progress with a soft falloff around each
// chapter's center, so neighboring blocks overlap and dissolve across
// the boundary instead of snapping on index change.
//
// Mounted between the WebGL canvas (z=0) and the HUD layer (z=100).
// The layer is decorative: pointer-events are disabled.

import { useEffect, useRef, useState } from 'react';
import { SCENES } from './planetSections';
import { useReducedMotion } from 'motion/react';

// Large graphic fields are punctuation, not wallpaper. Keeping them on only
// three chapters gives the planet and its terrain more room to read.
const COLOR_BLOCK_SCENES = new Set(['intro', 'projects', 'timeline']);

declare global {
  interface Window {
    __planetSceneIndex?: number;
    __planetProgress?: number;
  }
}

/** Soft window around a scene: full in the middle, fades past the edges
 *  so adjacent chapters briefly coexist during the handoff. */
function softBlockOpacity(progress: number, start: number, end: number): number {
  const mid = (start + end) * 0.5;
  const half = Math.max((end - start) * 0.5, 0.001);
  // Allow a little bleed past the official boundary for overlap.
  const reach = half * 1.35;
  const dist = Math.abs(progress - mid);
  if (dist <= half * 0.55) return 1;
  if (dist >= reach) return 0;
  const t = (dist - half * 0.55) / (reach - half * 0.55);
  // Smoothstep falloff.
  const s = t * t * (3 - 2 * t);
  return 1 - s;
}

export function ColorBlockLayer() {
  const [mode, setMode] = useState<'narrative' | 'exploration'>(
    typeof window !== 'undefined' && window.__planetMode === 'exploration'
      ? 'exploration'
      : 'narrative',
  );
  const [activeIdx, setActiveIdx] = useState(0);
  const reducedMotion = useReducedMotion();
  const layerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setActiveIdx(window.__planetSceneIndex ?? 0);

    let raf = 0;
    const tick = () => {
      const m =
        window.__planetMode === 'exploration' ? 'exploration' : 'narrative';
      setMode((prev) => (prev === m ? prev : m));
      const idx = window.__planetSceneIndex ?? 0;
      setActiveIdx((prev) => (prev === idx ? prev : idx));

      const root = layerRef.current;
      if (root && m === 'narrative' && !reducedMotion) {
        const p =
          typeof window.__planetProgress === 'number'
            ? window.__planetProgress
            : 0;
        const blocks = root.querySelectorAll<HTMLElement>(
          '.color-block-layer__block',
        );
        blocks.forEach((el) => {
          const scene = SCENES[Number(el.dataset.sceneIndex)];
          if (!scene) return;
          const op = softBlockOpacity(p, scene.start, scene.end);
          el.style.opacity = String(op * scene.colorBlockOpacity);
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reducedMotion]);

  return (
    <div
      ref={layerRef}
      className="color-block-layer"
      aria-hidden="true"
      data-mode={mode}
      style={reducedMotion ? { transition: 'none' } : undefined}
    >
      {SCENES.map((scene, i) => {
        if (!COLOR_BLOCK_SCENES.has(scene.key)) return null;
        const isActive =
          mode === 'exploration' ? i === activeIdx : i === activeIdx;
        // Reduced motion / exploration: binary active. Narrative uses
        // the rAF soft falloff above (inline opacity overrides this).
        const targetOpacity =
          mode === 'exploration' || reducedMotion
            ? isActive
              ? scene.colorBlockOpacity
              : 0
            : 0;
        return (
          <div
            key={scene.id}
            className={[
              'color-block-layer__block',
              `color-block-layer__block--${scene.colorBlockPosition}`,
              isActive ? 'is-active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              opacity: targetOpacity,
              transition:
                mode === 'exploration' || reducedMotion
                  ? reducedMotion
                    ? 'none'
                    : 'opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  : 'none',
              ['--block-color' as string]: scene.colorBlockColor,
            }}
            data-scene={scene.key}
            data-scene-index={i}
            data-active={isActive ? 'true' : 'false'}
          />
        );
      })}
    </div>
  );
}

export default ColorBlockLayer;
